import { Divider, ScrollArea, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useScrollShadow } from 'hooks/use-scroll-shadow';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { Fragment } from 'react';
import { type StepType, type TemplateType, useLoadTemplates } from './api';
import { CreateTemplateModal } from './CreateTemplateModal';
import { TemplateGroup } from './TemplateGroup';

type TemplateGroupListProps = {
  templateType: TemplateType;
  stepTypes: readonly StepType[];
  searchQuery: string;
};

export const TemplateGroupList = ({
  templateType,
  stepTypes,
  searchQuery,
}: TemplateGroupListProps) => {
  const { t } = useTranslation();
  const { templates, isTemplatesInitialLoading } = useLoadTemplates({ templateType });

  const normalizedQuery = normalizeSearchValue(searchQuery);

  const { topShadow, bottomShadow, scrollableRef } = useScrollShadow(
    `${isTemplatesInitialLoading}:${templates.length}:${normalizedQuery}`,
    'light',
  );

  const openCreateModal = (stepType: StepType) =>
    modals.open({
      title: t(i18nKeys.NEW_TEMPLATE),
      centered: true,
      children: (
        <CreateTemplateModal
          stepType={stepType}
          templateType={templateType}
          onClose={modals.closeAll}
        />
      ),
    });

  return (
    <ScrollArea h="100%" type="hover" offsetScrollbars="y" viewportRef={scrollableRef}>
      {topShadow}
      {bottomShadow}
      <Stack gap="md" pt="md" pb="xl">
        {stepTypes.map((stepType, index) => (
          <Fragment key={stepType}>
            {index > 0 && <Divider my="xs" />}
            <TemplateGroup
              title={t(tKeysLoose.TEMPLATE_TYPES[stepType.toUpperCase()])}
              templates={templates.filter(
                template =>
                  template.stepType === stepType &&
                  normalizeSearchValue(template.name).includes(normalizedQuery),
              )}
              onClickNew={() => openCreateModal(stepType)}
              isLoading={isTemplatesInitialLoading}
            />
          </Fragment>
        ))}
      </Stack>
    </ScrollArea>
  );
};

function normalizeSearchValue(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
